import { h } from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { useShopware } from '../shopware/ShopwareContext';
import { useJsonProvider } from '../JsonProvider';
import { fetchPdpData, getCountryParamFromUrl, type PdpVariantData } from '../shopware/pdpData';
import { useEnsureCustomerLogin } from '../shopware/useEnsureCustomerLogin';
import { checkProductAvailability } from '../shopware/shopwareUtils';
import { buildGA4Item, pushAddToCart } from '../tracking/ecommerceDataLayer';
import {
  getBuyableVariants,
  getPreselectedVariant,
  getPriceMode,
  isLoadingHoldFromUrl,
  readAddToCartPayload,
  readSnapshotEntry,
  readSnapshotONumberEntry,
  slotSelector,
  type Variant,
} from './addToCartData';
import {
  PriceLine,
  PriceLineSkeleton,
  priceViewFromONumber,
  priceViewFromPdp,
  priceViewFromSnapshot,
} from './PriceLine';
import { CtaButton } from './CtaButton';
import { AddToCartModal } from './AddToCartModal';

// Instance counter for DOM ids: the same product can sit in several bricks on one page (ORT-3055)
let instanceCounter = 0;

type PdpMap = Record<string, PdpVariantData>;
type PdpStatus = 'idle' | 'loading' | 'ready' | 'error';

interface AddToCartIslandProps {
  /** The brick instance's root element (`data-add-to-cart-root`), see addToCartData.ts */
  root: HTMLElement;
}

/**
 * ORT-3057 / ORT-3055: one island per brick instance. Owns the state (variants, prices, selection) and
 * renders into the root's slots via portals: the price line, the CTA button, and the modal (body).
 * Reads only from its own root and the page-level JSONs, no document-wide ids, so many instances per
 * page work.
 *
 * Price source (data-add-to-cart-prices):
 * - live (default, hero): one /pdp-data call on mount for this product's pack sizes.
 * - snapshot (overview cards): first paint from page-product-prices-json, no Shopware call on page load.
 * In both modes opening the modal / adding needs live /pdp-data (Shopware ids, stock, customer prices),
 * fetched once per island on first interaction if not loaded yet.
 */
export const AddToCartIsland = ({ root }: AddToCartIslandProps) => {
  // isAddingToCart is page-wide (one cart): while any island or the PDP adds, every CTA waits. The
  // context runs one add at a time and skips a concurrent one, so an enabled second button would
  // have been a dead click at best.
  const { addProduct, openDrawer, isAddingToCart } = useShopware();
  const { pageProductPrices } = useJsonProvider();
  const ensureCustomerLogin = useEnsureCustomerLogin();

  const [data] = useState(() => readAddToCartPayload(root));
  const [instanceId] = useState(() => `add-to-cart-${root.dataset.addToCartProduct || 'x'}-${++instanceCounter}`);
  const priceMode = getPriceMode(root);
  const variants = useMemo(() => (data ? getBuyableVariants(data.dosageForms) : []), [data]);
  // ORT-3175: pack size preselected by the editor; price, stock and the modal's initial selection follow it
  const preselected = useMemo(() => getPreselectedVariant(root, variants), [root, variants]);
  // Slots are resolved (and their Twig skeletons cleared) in an effect, and only then rendered into.
  // Clearing after the portals had rendered would wipe Preact's own output (same pattern as ShopApp).
  const [slots, setSlots] = useState<{ price: HTMLElement | null; button: HTMLElement | null } | null>(null);
  // Slot contract (set by Twig): text button with editor label/style (hero) or icon button with the
  // placeholder's class list (card); compact price line (card)
  const buttonLabel = slots?.button?.dataset.label || 'In den Warenkorb';
  const buttonStyle = slots?.button?.dataset.btnStyle || 'primary';
  const buttonVariant: 'text' | 'icon' = slots?.button?.dataset.variant === 'icon' ? 'icon' : 'text';
  const buttonClass = slots?.button?.dataset.btnClass || undefined;
  const compactPrice = slots?.price?.dataset.compact !== undefined;

  const isShopEnabled = typeof window !== 'undefined' && (window as any).SHOP_ENABLED !== false;

  const [pdp, setPdp] = useState<PdpMap | null>(null);
  const [pdpStatus, setPdpStatus] = useState<PdpStatus>('idle');
  const pdpPromiseRef = useRef<Promise<PdpMap | null> | null>(null);
  const [selected, setSelected] = useState<Variant | null>(preselected ?? variants[0] ?? null);
  const [modalOpen, setModalOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  // Set when an add from the modal succeeded: the cart drawer opens once the modal has faded out
  // (closeModal), so the two sheets never overlap on mobile and the drawer's scroll lock is not
  // undone by the modal's unmount cleanup.
  const drawerAfterCloseRef = useRef(false);

  // Snapshot aggregate for this product (overview cards), null in live mode or without a usable snapshot
  const snapshot = useMemo(
    () =>
      priceMode === 'snapshot' && root.dataset.addToCartProduct
        ? readSnapshotEntry(pageProductPrices.data, root.dataset.addToCartProduct, getCountryParamFromUrl())
        : null,
    [priceMode, pageProductPrices.data, root]
  );
  // The preselected pack's own snapshot entry; null without one (then the product aggregate applies)
  const preselectedSnapshot = useMemo(
    () =>
      priceMode === 'snapshot' && preselected
        ? readSnapshotONumberEntry(pageProductPrices.data, preselected.packsize.oNumber, getCountryParamFromUrl())
        : null,
    [priceMode, pageProductPrices.data, preselected]
  );

  // Take over the slots: drop the Twig skeletons, then render into the now empty elements.
  useEffect(() => {
    const price = root.querySelector<HTMLElement>(slotSelector('price'));
    const button = root.querySelector<HTMLElement>(slotSelector('button'));
    if (price) price.innerHTML = '';
    if (button) button.innerHTML = '';
    setSlots({ price, button });
  }, [root]);

  // One batched /pdp-data call for all buyable pack sizes: live prices, stock and the Shopware ids
  // needed for addProduct(). Like the PDP: logged-in shoppers get the customer context first so
  // customer-specific prices apply; a failed login falls back to the anonymous (cacheable) path.
  // Deduplicated: mount (live mode) and first interaction (snapshot mode) share one promise.
  const loadPdp = useCallback((): Promise<PdpMap | null> => {
    if (pdpPromiseRef.current) return pdpPromiseRef.current;
    setPdpStatus('loading');
    const promise = ensureCustomerLogin()
      .then((withCustomerToken) =>
        fetchPdpData(
          variants.map((variant) => variant.packsize.oNumber),
          getCountryParamFromUrl(),
          { withCustomerToken }
        )
      )
      .then((map) => {
        setPdp(map);
        setPdpStatus('ready');
        // Preselect the first variant that can actually be bought. An editor's preselection stays, even
        // when sold out: the CTA then shows the sold-out state instead of offering a different pack.
        setSelected((current) => {
          if (preselected) return current;
          if (current && checkProductAvailability(map[current.packsize.oNumber]?.product ?? null)) return current;
          return variants.find((v) => checkProductAvailability(map[v.packsize.oNumber]?.product ?? null)) ?? current;
        });
        return map;
      })
      .catch((err) => {
        console.error('[eshop] [add-to-cart] /pdp-data failed', err);
        setPdpStatus('error');
        pdpPromiseRef.current = null; // let a later interaction retry
        return null;
      });
    pdpPromiseRef.current = promise;
    return promise;
  }, [variants, preselected, ensureCustomerLogin]);

  // Live mode: fetch on mount, exactly once. Snapshot mode: nothing until the shopper interacts.
  // The once-guard is deliberate: a failed fetch nulls pdpPromiseRef so the next CLICK can retry, but the
  // mount effect must not retry on its own. It re-runs whenever a dependency changes identity, and without
  // the guard an error -> re-render -> effect -> fetch -> error cycle hammered /pdp-data while Shopware was down.
  const autoLoadedRef = useRef(false);
  useEffect(() => {
    if (autoLoadedRef.current) return;
    if (priceMode !== 'live' || !isShopEnabled || variants.length === 0) return;
    // ?atc=loading: stay in the loading state forever (skeleton inspection), see addToCartData.ts
    if (isLoadingHoldFromUrl()) return;
    autoLoadedRef.current = true;
    void loadPdp();
  }, [priceMode, isShopEnabled, variants, loadPdp]);

  // Graceful degradation -> plain link to the PDP, no price: no shop, no buyable variant, a failed
  // /pdp-data call (on mount in live mode, on click in snapshot mode), or no usable snapshot (snapshot).
  const fallback =
    !isShopEnabled || variants.length === 0 || pdpStatus === 'error' || (priceMode === 'snapshot' && snapshot === null);
  // Price skeleton: only the live first paint waits for Shopware
  const loading = !fallback && priceMode === 'live' && pdp === null;
  // Sold out = no buyable variant. Once /pdp-data is loaded (mount in live mode, first click in snapshot
  // mode) its live stock decides, exactly like the PDP's checkProductAvailability(); before that, the
  // snapshot's anyAvailable. Without this the hero (live mode) kept an active CTA for a sold-out product
  // and a single-variant click "added" an item Shopware then dropped from the cart.
  // Also with a preselection (ORT-3175): a sold-out preselected pack keeps the CTA active while another
  // pack is buyable, the modal opens on the preselected pack (shown as unavailable) and the shopper switches.
  const soldOut =
    (pdp !== null && !variants.some((v) => checkProductAvailability(pdp[v.packsize.oNumber]?.product ?? null))) ||
    (priceMode === 'snapshot' && snapshot !== null && !snapshot.anyAvailable);
  // Greyed out button (Christian's .out-of-stock state) when sold out, and also when the card has no
  // snapshot entry at all: the button is then the PDP fallback link, and .out-of-stock's
  // pointer-events: none lets the click fall through to the card's stretched link.
  const outOfStock = soldOut || (priceMode === 'snapshot' && snapshot === null);

  // The snapshot's price is its cheapest variant. When that one is sold out but a dearer one is not
  // (anyAvailable && !fromAvailable, e.g. Orthomol Pro Metabol), the price would be advertised next to an
  // active button that cannot buy it: show no price, keep the button, the modal shows the live prices.
  // A product with nothing available keeps its price next to the sold-out button, like the PDP.
  const snapshotPriceBuyable = snapshot !== null && (snapshot.fromAvailable || !snapshot.anyAvailable);
  // A preselected pack shows its own price (no "ab"), also when sold out, like the PDP. Without a snapshot
  // entry for it no price at all (like live mode): the aggregate would be another pack's price.
  const priceView = fallback
    ? null
    : priceMode === 'snapshot'
      ? preselected
        ? preselectedSnapshot && priceViewFromONumber(preselectedSnapshot, preselected)
        : snapshot && snapshotPriceBuyable && priceViewFromSnapshot(snapshot, variants)
      : pdp && priceViewFromPdp(preselected ? [preselected] : variants, pdp);
  const hidePrice = fallback || (!loading && !priceView);

  useEffect(() => {
    if (!slots?.price) return;
    slots.price.setAttribute('aria-busy', String(loading));
    slots.price.hidden = hidePrice;
  }, [slots, loading, hidePrice]);

  // PDP link for the fallback: from the variants payload, or from the root when that payload is missing
  const fallbackUrl = data?.product.url || root.dataset.addToCartUrl || null;

  // Returns true on success; the modal closes itself (with fade-out) based on that.
  // Direct add (single variant, no modal): the drawer opens right away as feedback. From the modal:
  // the drawer is deferred to closeModal, after the modal's fade-out.
  const handleAdd = useCallback(
    async (variant: Variant, quantity: number, map: PdpMap | null = pdp, fromModal = false): Promise<boolean> => {
      const shopwareProduct = map?.[variant.packsize.oNumber]?.product ?? null;
      if (!shopwareProduct?.id) {
        console.error('[eshop] [add-to-cart] no Shopware product for', variant.packsize.oNumber);
        alert('Fehler: Produkt konnte nicht hinzugefügt werden');
        return false;
      }
      setAdding(true);
      try {
        // Same call as the PDP: pass the pack size o-number so promo scoping works (ORT-3077).
        // null = the context skipped this add because another one was in flight (see addProduct):
        // nothing landed in the cart, so no drawer, no GA4 event, no success.
        const result = await addProduct(shopwareProduct.id, quantity, variant.packsize.oNumber);
        if (!result) {
          console.warn('[eshop] [add-to-cart] add skipped, another add was in progress', variant.packsize.oNumber);
          return false;
        }
        if (!fromModal) openDrawer();
        const ga4Item = buildGA4Item(variant.packsize, variant.dosageForm, shopwareProduct, quantity);
        if (ga4Item) pushAddToCart(ga4Item);
        if (fromModal) drawerAfterCloseRef.current = true;
        return true;
      } catch (err) {
        console.error('[eshop] [add-to-cart] addProduct failed', err);
        alert('Fehler beim Hinzufügen zum Warenkorb');
        return false;
      } finally {
        setAdding(false);
      }
    },
    [pdp, addProduct, openDrawer]
  );

  // CTA: make sure live data is there (already loaded in live mode, fetched now in snapshot mode), then
  // with exactly one option the product goes straight into the cart (ticket), otherwise open the modal.
  const handleCtaClick = async () => {
    const map = pdp ?? (await loadPdp());
    if (!map) return; // fetch failed: pdpStatus 'error' -> the button re-renders as a link to the PDP
    if (variants.length === 1) {
      // Direct add only when the one variant can be bought. Otherwise nothing happens here: setPdp(map)
      // in loadPdp() already re-rendered the CTA as sold out (see soldOut), the PDP's disabled state.
      if (!checkProductAvailability(map[variants[0].packsize.oNumber]?.product ?? null)) return;
      void handleAdd(variants[0], 1, map);
      return;
    }
    setModalOpen(true);
  };

  // Called by the modal after its fade-out finished. Unmounting the modal and opening the drawer in
  // the same tick means the drawer's body scroll lock is applied after the modal's cleanup released it.
  const closeModal = useCallback(() => {
    setModalOpen(false);
    if (drawerAfterCloseRef.current) {
      drawerAfterCloseRef.current = false;
      openDrawer();
    }
  }, [openDrawer]);

  const handleModalAdd = useCallback(
    (variant: Variant, quantity: number) => handleAdd(variant, quantity, pdp, true),
    [handleAdd, pdp]
  );

  // Without the variants payload (missing JSON, or the collector swallowed a build error) the island still
  // takes over the slots: the Twig skeleton is a disabled button, so it renders the PDP fallback link
  // instead (fallback is true, variants is empty). Only with no URL at all is there nothing to offer.
  if (!slots || (!data && !fallbackUrl)) return null;

  return (
    <>
      {slots.price &&
        createPortal(
          loading ? (
            <PriceLineSkeleton />
          ) : priceView ? (
            <PriceLine view={priceView} compact={compactPrice} />
          ) : null,
          slots.price
        )}
      {slots.button &&
        createPortal(
          <CtaButton
            variant={buttonVariant}
            label={soldOut ? 'Bald wieder verfügbar' : buttonLabel} // PDP wording; visible text on the hero, aria-label on the card
            btnStyle={soldOut ? 'secondary' : buttonStyle} // PDP: sold-out button is btn-secondary (card icon keeps its own .out-of-stock grey)
            className={buttonClass}
            onClick={handleCtaClick}
            disabled={loading || adding || isAddingToCart || pdpStatus === 'loading'}
            busy={loading || adding || isAddingToCart || pdpStatus === 'loading'}
            outOfStock={outOfStock}
            fallbackHref={fallback ? fallbackUrl : null}
          />,
          slots.button
        )}
      {modalOpen &&
        data &&
        selected &&
        pdp &&
        createPortal(
          <AddToCartModal
            id={`${instanceId}-modal`}
            product={data.product}
            variants={variants}
            selected={selected}
            onSelect={setSelected}
            pdp={pdp}
            adding={adding || isAddingToCart}
            onAdd={handleModalAdd}
            onClose={closeModal}
          />,
          document.body
        )}
    </>
  );
};
